import ReactGA from 'react-ga4';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from './logger';
import { getCookieConsent, COOKIE_CATEGORIES } from './cookieUtils';
import { API_BASE_URL, isLocal } from '../config/env';

// Track if GA has been initialized
let isGAInitialized = false;

// Configure GA4 with debug mode or production mode
export const initGA = () => {
  const consent = getCookieConsent();
  
  // Only initialize GA if user has consented to analytics
  if (consent && consent[COOKIE_CATEGORIES.ANALYTICS]) {
    if (!isGAInitialized) {
      ReactGA.initialize('G-DGQ14FN3XS', {
        debug: false,
        testMode: false,
      });
      isGAInitialized = true;
      logger.log('GA4 initialized with user consent');
    }
  } else {
    // If no consent or analytics declined, ensure GA is not initialized
    if (isGAInitialized) {
      logger.log('GA4 disabled - no analytics consent');
    }
  }
};

// Disable GA tracking completely
export const disableGA = () => {
  if (isGAInitialized) {
    // Clear any existing GA data
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      });
    }
    isGAInitialized = false;
    logger.log('GA4 disabled - analytics consent withdrawn');
  }
};

// Check if GA tracking is enabled
export const isGAEnabled = () => {
  return isGAInitialized;
};

// Safe GA event tracking - only tracks if consent is given
export const trackEvent = (eventName, parameters = {}) => {
  if (isGAInitialized) {
    ReactGA.event({
      action: eventName,
      ...parameters
    });
    debugLog('GA4 Event tracked:', { eventName, parameters });
  } else {
    debugLog('GA4 Event skipped - no consent:', { eventName, parameters });
  }
};

// Debug logger function
export const debugLog = (message, data) => {
  if (isLocal) {
    logger.log(`🔍 ${message}`, data);
  }
};

// Separate the debugger into its own component with debug-only rendering
export const GADebugger = () => {
    const [events, setEvents] = useState([]);
    const [isMinimized, setIsMinimized] = useState(false);
  
    useEffect(() => {
      if (isLocal) {
        const originalSend = ReactGA.send;
        ReactGA.send = (...args) => {
          setEvents(prev => [...prev, {
            timestamp: new Date().toISOString(),
            data: args[0]
          }]);
          originalSend.apply(ReactGA, args);
        };
  
        return () => {
          ReactGA.send = originalSend;
        };
      }
    }, []);
  
    // Only render in local environments
    // Disabled during demo video recording to avoid debugger showing up in videos
    if (!isLocal || true) {
      return null; 
    }
  
    return (
      <div style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        background: '#f0f0f0',
        padding: 15,
        borderRadius: 5,
        maxHeight: isMinimized ? 40 : 300,
        overflowY: 'auto',
        zIndex: 9999,
        fontSize: 12
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: isMinimized ? 0 : 10 
        }}>
          <h4 style={{ margin: 0 }}>GA4 Debug Events</h4>
          <button 
            onClick={() => setIsMinimized(!isMinimized)}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            {isMinimized ? '🔽' : '🔼'}
          </button>
        </div>
        {!isMinimized && events.map((event, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div>{event.timestamp}</div>
            <pre>{JSON.stringify(event.data, null, 2)}</pre>
          </div>
        ))}
      </div>
    );
  };
  
// Enhanced page view tracking with debug logs
export const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname || "/";
    const trackable =
      path === "/" ||
      path === "/contact" ||
      path === "/products" ||
      path === "/subscription" ||
      path.startsWith("/solutions/");
    if (trackable) {
      fetch(`${API_BASE_URL}/api/public/site-pageview/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path }),
        keepalive: true,
      }).catch(() => {});
    }

    if (isGAInitialized) {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname,
        title: document.title
      });

      debugLog('GA4 Pageview:', {
        page: location.pathname,
        title: document.title,
        timestamp: new Date().toISOString()
      });
    } else {
      debugLog('GA4 Pageview skipped - no consent:', {
        page: location.pathname,
        title: document.title,
        timestamp: new Date().toISOString()
      });
    }
  }, [location]);

  return null;
};
